import axios from "axios";
import { decryptCookie, encryptCookie } from "../utils/cookie";
import { DOMAIN } from "../strings/CONSTANTS";

export const AdminLogin = async (data: any) => {
  try {
    const resp = await axios({
      url: `${DOMAIN}/admin/login`,
      method: "POST",
      data: data,
    });

    if (resp.status === 200) {
      const token = await resp.data.token;
      encryptCookie("token", token);
      return resp.data;
    }
    return null;
  } catch (err: any) {
    return err.message;
  }
};

export const send_Notifications = async (data: any) => {
  try {
    const resp = await axios({
      url: `${DOMAIN}/send-notifications`,
      method: "POST",
      headers: {
        authorization: decryptCookie("token"),
      },
      data: data,
    });
    return resp.data;
  } catch (error) {
    throw error;
  }
};

export const get_all_users_count = async () => {
  const resp = await axios(`${DOMAIN}/user/get-users-count`);
  return resp.data.count;
};

export const delete_user_by_id = async (userId: string) => {
  const resp = await fetch(`${DOMAIN}/admin/delete-user/${userId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!resp.ok) {
    throw new Error(`Failed to delete user: ${resp.statusText}`);
  }

  return await resp.json();
};

export const get_all_users = async ({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}) => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    search,
  }).toString();

  const resp = await fetch(`${DOMAIN}/get-all-users?${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!resp.ok) {
    throw new Error(`Failed to fetch users: ${resp.statusText}`);
  }

  return await resp.json();
};

export const get_Ad = async () => {
  const response = await axios.get(`${DOMAIN}/get-ad`);
  return response.data.data;
};

export const get_ad_network = async () => {
  const response = await axios.get(`${DOMAIN}/fetch_network`);
  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to fetch ad network.");
  }
  return response.data.data;
};

export const update_ad_network = async (network: string) => {
  const response = await axios.post(`${DOMAIN}/update_network`, {
    ad_network: network,
  });
  if (!response.data.success) {
    throw new Error(response.data.message || "Failed to update ad network.");
  }
  return response.data.data;
};

export const get_all_qr_designs = async () => {
  const resp = await axios.post(`${DOMAIN}/get-all-qr-designs`);
  return resp.data.data;
};

export const create_qr_design = async (data: {
  name: string;
  qrColor: string;
  logoBase64?: string;
}) => {
  const resp = await axios.post(`${DOMAIN}/create-qr-design`, data, {
    headers: { authorization: decryptCookie("token") },
  });
  return resp.data.data;
};

/// STICKERS APIS

export const getAllCategories = async () => {
  try {
    const resp = await axios.get(`${DOMAIN}/category/list`);
    return resp.data.data;
  } catch (err: any) {
    throw err;
  }
};

export const createCategory = async (data: {
  name: string;
  imageBase64: string;
}) => {
  try {
    const resp = await axios({
      url: `${DOMAIN}/category/create`,
      method: "POST",
      headers: {
        authorization: decryptCookie("token"),
      },
      data,
    });

    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

export const deleteCategory = async (id: string) => {
  try {
    const resp = await axios.delete(`${DOMAIN}/category/delete/${id}`, {
      headers: {
        authorization: decryptCookie("token"),
      },
    });
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

export const getStickerReports = async ({
  status = "all",
  page = 1,
  limit = 20,
}: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const query = new URLSearchParams({
      status,
      page: String(page),
      limit: String(limit),
    }).toString();

    const resp = await axios.get(`${DOMAIN}/admin/sticker-reports?${query}`, {
      headers: {
        authorization: decryptCookie("token"),
      },
    });
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

// Update sticker report status
export const updateStickerReportStatus = async (
  id: string,
  status: string,
  notes: string
) => {
  try {
    const resp = await axios.put(
      `${DOMAIN}/admin/sticker-reports/${id}`,
      { status, notes },
      {
        headers: {
          authorization: decryptCookie("token"),
        },
      }
    );
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

// Get sticker report statistics
export const getStickerReportStats = async () => {
  try {
    const resp = await axios.get(
      `${DOMAIN}/admin/sticker-reports/stats/summary`,
      {
        headers: {
          authorization: decryptCookie("token"),
        },
      }
    );
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

// Smart Link Reports APIs
export const getSmartLinkReports = async ({
  status = "all",
  page = 1,
  limit = 20,
}: {
  status?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const query = new URLSearchParams({
      status,
      page: String(page),
      limit: String(limit),
    }).toString();

    const resp = await axios.get(
      `${DOMAIN}/admin/smart-link-reports?${query}`,
      {
        headers: {
          authorization: decryptCookie("token"),
        },
      }
    );
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

// Update smart link report status
export const updateSmartLinkReportStatus = async (
  id: string,
  status: string,
  notes: string
) => {
  try {
    const resp = await axios.put(
      `${DOMAIN}/admin/smart-link-reports/${id}`,
      { status, notes },
      {
        headers: {
          authorization: decryptCookie("token"),
        },
      }
    );
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

// Get smart link report statistics
export const getSmartLinkReportStats = async () => {
  try {
    const resp = await axios.get(
      `${DOMAIN}/admin/smart-link-reports/stats/summary`,
      {
        headers: {
          authorization: decryptCookie("token"),
        },
      }
    );
    return resp.data;
  } catch (err: any) {
    throw err;
  }
};

//SurpriseQRReports

export const getSurpriseQRReports = (params: any) =>
  axios.get(`${DOMAIN}/admin/surprise-qr-reports`, { params }).then(res => res.data);

export const getSurpriseQRReportStats = () =>
  axios.get(`${DOMAIN}/admin/surprise-qr-reports/stats`).then(res => res.data);

export const updateSurpriseQRReportStatus = (id: string, status: string, notes: string) =>
  axios.put(`${DOMAIN}/admin/surprise-qr-reports/${id}`, { status, notes }).then(res => res.data);